/**
 * Counts a number up to its final value once, the moment it scrolls into
 * view — the way a large figure on a keynote slide lands.
 *
 * Deliberately not tied to editorial-reveal's shared observer: a count-up is
 * commonly nested inside an element that observer already reveals, and a
 * second one-shot observer on the number itself is cheap and keeps this
 * component usable on its own, anywhere in the theme.
 *
 * The final value is already correct in the rendered HTML, so the animation
 * is a progressive enhancement, not a source of truth: prefers-reduced-motion,
 * a missing IntersectionObserver, or content that doesn't start with a
 * number (a size, a symbol) all leave the static text exactly as rendered.
 */
class CountUp extends HTMLElement {
  connectedCallback() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (!('IntersectionObserver' in window)) return;

    const raw = this.textContent?.trim() ?? '';
    const match = raw.match(/^(-?\d+)(.*)$/s);
    if (!match) return;

    const target = Number(match[1]);
    const suffix = match[2];
    if (!Number.isFinite(target)) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          observer.disconnect();
          this.#run(target, suffix);
        }
      },
      { rootMargin: '0px 0px -10% 0px' }
    );
    observer.observe(this);
  }

  /**
   * @param {number} target
   * @param {string} suffix
   */
  #run(target, suffix) {
    const duration = 900;
    const start = performance.now();
    // A quartic ease-out: fast start, settling in rather than ticking evenly.
    const ease = (/** @type {number} */ t) => 1 - (1 - t) ** 4;

    const step = (/** @type {number} */ now) => {
      const progress = Math.min(1, (now - start) / duration);
      this.textContent = Math.round(target * ease(progress)) + suffix;
      if (progress < 1) requestAnimationFrame(step);
      else this.textContent = target + suffix;
    };
    requestAnimationFrame(step);
  }
}

if (!customElements.get('count-up')) {
  customElements.define('count-up', CountUp);
}
